/**
 * The whole-book level match, as a state machine.
 *
 * Kept out of the studio on purpose. The studio owns the *book* — pages, blocks,
 * the cover — and this owns the *operation*, which has six states, three of them
 * long-running, and a failure mode for every clip. Threading that through a
 * 3000-line screen would make the one thing that can go wrong per page
 * impossible to see.
 *
 * The dialog is correspondingly dumb: it renders whatever phase this is in. That
 * is also how the studio's other dialogs work, and it means the state machine can
 * be reasoned about — and the states rendered — without a book.
 */
import {useCallback, useRef, useState} from 'react'

import {type AudioOpenError} from '#/lib/flashbookAudioEditor'
import {
  applyLevelPlan,
  COVER_CLIP_ID,
  type LevelledReadingClip,
  type LevelProgress,
  measureReadingClips,
  type ReadingClip,
  type ReadingClipFailure,
  readingClipsOf,
} from '#/lib/flashbookLevelMatch'
import {
  bookTargetLufs,
  type LevelMatchPlan,
  planLevelMatch,
} from '#/lib/flashbookLoudness'

export type LevelMatchPhase =
  /** Nothing has been run — the dialog is not open, or has just opened. */
  | 'idle'
  /** Reading every page's audio. */
  | 'measuring'
  /** Measured, planned, and waiting for the creator to say yes. */
  | 'review'
  /** Rewriting the pages the plan moves. */
  | 'applying'
  /** Done — and undoable until the dialog is dismissed. */
  | 'done'
  /** Nothing runs here at all: this build has no audio engine. */
  | 'failed'

export type LevelMatchState = {
  phase: LevelMatchPhase
  /** Clips finished, and clips to do, for whichever sweep is running. */
  done: number
  total: number
  /** How many readings the book has — the dialog explains a single one. */
  clipCount: number
  /**
   * The readings themselves, so a row can say "Page 4" rather than a block id.
   *
   * Carried in state rather than in a ref because the dialog renders it: the
   * plan speaks in ids, and a creator deciding whether to rewrite their book
   * needs to know which page is about to move.
   */
  clips: ReadingClip[]
  plan: LevelMatchPlan | null
  /** Clips that could not be read or rewritten, so the count is never a lie. */
  failures: ReadingClipFailure[]
  /** Why the pass could not run at all. */
  problem: AudioOpenError | null
  /** How many recordings were actually rewritten. */
  applied: number
  /**
   * The spread the book measures *after* the rewrite, in LU.
   *
   * Measured, not predicted. The planner already predicts where each clip lands
   * and the render is an exact multiply, so the prediction would usually be
   * right — but "usually" is not what a success screen is for. This is a second
   * measurement pass over the clips the book now holds, which makes the number on
   * screen a finding about the creator's book rather than an expectation of it,
   * and which also catches a clip whose render quietly failed.
   *
   * `null` until the verification has run, and left `null` if it could not.
   */
  verifiedSpread: number | null
}

const INITIAL: LevelMatchState = {
  phase: 'idle',
  done: 0,
  total: 0,
  clipCount: 0,
  clips: [],
  plan: null,
  failures: [],
  problem: null,
  applied: 0,
  verifiedSpread: null,
}

export type UseFlashBookLevelMatchOptions = {
  /** The book, in the shape the pass needs — pages of blocks that carry audio. */
  pages: readonly {blocks: readonly {id: string; readingAudioUrl?: string}[]}[]
  /** The cover's spoken introduction, when the cover is a reading. */
  coverAudioUri?: string
  /**
   * Rewrites the level-matched clips.
   *
   * The hook hands back ids and URLs and the studio decides what an id means:
   * a block on some page, or the cover. Awaited, so the upload that makes the
   * clip durable is finished before the pass reports itself done — a creator who
   * is told it worked must not lose it by closing the tab.
   */
  onLevelled: (levelled: LevelledReadingClip[]) => Promise<void> | void
  /** Puts the originals back, for undo. */
  onRestore: (clips: {id: string; url: string}[]) => void
}

export type FlashBookLevelMatch = {
  state: LevelMatchState
  /** Measures the book and plans the match. Safe to call from `idle` or `done`. */
  start: () => void
  /** Runs the plan that `start` produced. */
  apply: () => void
  /** Puts every rewritten page back to the clip it had. */
  undo: () => void
  /** Back to `idle`, so reopening the dialog measures afresh. */
  reset: () => void
}

export function useFlashBookLevelMatch({
  pages,
  coverAudioUri,
  onLevelled,
  onRestore,
}: UseFlashBookLevelMatchOptions): FlashBookLevelMatch {
  const [state, setState] = useState<LevelMatchState>(INITIAL)
  /**
   * Guards a run in flight.
   *
   * Closing the dialog mid-measure must not resurrect a plan when the last
   * decode settles — the creator cancelled, and a dialog that reopens on its own
   * with a stale plan is the kind of thing that makes a tool feel haunted.
   */
  const runRef = useRef(0)
  /** The clips as they were measured, so undo restores the exact URLs. */
  const originalsRef = useRef<ReadingClip[]>([])

  const clipsOfBook = useCallback(
    () => readingClipsOf(pages, coverAudioUri),
    [coverAudioUri, pages],
  )

  const start = useCallback(() => {
    const run = runRef.current + 1
    runRef.current = run
    const clips = clipsOfBook()
    originalsRef.current = clips
    setState({
      ...INITIAL,
      phase: 'measuring',
      total: clips.length,
      clipCount: clips.length,
      clips,
    })

    const onProgress: LevelProgress = (done, total) => {
      if (runRef.current !== run) return
      setState(previous => ({...previous, done, total}))
    }

    void (async () => {
      const {measured, failures} = await measureReadingClips(clips, onProgress)
      if (runRef.current !== run) return
      // The planner speaks in ids and measurements; the pass carries whole clips
      // so `apply` knows where the bytes are. One mapping, at the seam.
      const levels = measured.map(entry => ({
        id: entry.clip.id,
        measurement: entry.measurement,
      }))
      const plan = planLevelMatch(levels, bookTargetLufs(levels))
      setState({
        ...INITIAL,
        phase: 'review',
        clipCount: clips.length,
        clips,
        plan,
        failures,
      })
    })()
  }, [clipsOfBook])

  const apply = useCallback(() => {
    const plan = state.plan
    if (!plan || plan.isEmpty) return
    const run = runRef.current
    setState(previous => ({
      ...previous,
      phase: 'applying',
      done: 0,
      total: plan.changes.length,
    }))

    void (async () => {
      const {levelled, failures} = await applyLevelPlan(
        plan,
        originalsRef.current,
        (done, total) => {
          if (runRef.current !== run) return
          setState(previous => ({...previous, done, total}))
        },
      )
      // The studio writes the book before this is reported as done: a clip that
      // is durable in the PDS but not in the page would vanish on save.
      try {
        await onLevelled(levelled)
      } catch {
        // The page rewrite is the studio's problem and it says its own piece;
        // the pass still reports what it managed to render.
      }
      if (runRef.current !== run) return

      /*
       * Verify by measuring what the book holds *now*.
       *
       * The clip list is rebuilt here rather than re-read from the studio, which
       * has already moved on: a rewritten clip is measured at its new URL and an
       * untouched one — including one whose render failed — at the one it kept.
       */
      const rewritten = new Map(
        levelled.map(entry => [entry.clip.id, entry.url]),
      )
      const nextClips = originalsRef.current.map(clip => ({
        ...clip,
        url: rewritten.get(clip.id) ?? clip.url,
      }))
      let verifiedSpread: number | null = null
      let verifyFailures: ReadingClipFailure[] = []
      try {
        const remeasured = await measureReadingClips(nextClips)
        verifyFailures = remeasured.failures
        verifiedSpread = spreadOfPlan(remeasured.measured)
      } catch {
        // Left null: the screen then says what was rewritten without claiming a
        // number it did not check.
      }
      if (runRef.current !== run) return
      setState(previous => ({
        ...previous,
        phase: 'done',
        applied: levelled.length,
        verifiedSpread,
        failures: [...previous.failures, ...failures, ...verifyFailures],
      }))
    })()
  }, [onLevelled, state.plan])

  const undo = useCallback(() => {
    const plan = state.plan
    if (!plan) return
    const changed = new Set(plan.changes.map(change => change.id))
    const originals = originalsRef.current
      .filter(clip => changed.has(clip.id))
      .map(clip => ({id: clip.id, url: clip.url}))
    onRestore(originals)
    setState(previous => ({...previous, phase: 'review'}))
  }, [onRestore, state.plan])

  const reset = useCallback(() => {
    // Bumping the run invalidates anything still in flight.
    runRef.current += 1
    setState(INITIAL)
  }, [])

  return {state, start, apply, undo, reset}
}

/**
 * The spread across the clips a verification pass just measured.
 *
 * Written out rather than reached through `planLevelMatch`, because the question
 * here is not "what should happen" but "what is true" — and a plan built to
 * answer the first would report its own target if it had one.
 */
function spreadOfPlan(measured: {measurement: {lufs: number | null}}[]) {
  const levels = measured
    .map(entry => entry.measurement.lufs)
    .filter((lufs): lufs is number => lufs !== null)
  if (levels.length < 2) return null
  return Math.max(...levels) - Math.min(...levels)
}

/** The cover's clip id, so the studio can recognise it without importing twice. */
export {COVER_CLIP_ID}
